/**
 * Gestão de usuários — tela administrativa.
 *
 * Permite visualizar todos os usuários do Cognito, alterar perfil, cargo, trilha,
 * habilitar/desabilitar contas, upload de foto, criar novos e excluir. O usuário logado não pode
 * alterar a si próprio (proteção de interface; a API também recusa).
 */

import { Fragment, useCallback, useEffect, useState } from 'react';

import {
  validarFoto,
  aplicarMascaraTelefoneBr,
  exibicaoParaCampo,
  normalizarTelefoneBr,
  type FotoCandidata,
  type ProblemaFoto,
} from '@emaster/dominio';

import { listarUsuarios, alterarUsuario, criarUsuario, excluirUsuario } from '../api/cliente.ts';
import { useAutenticacao } from '../autenticacao/contexto.tsx';

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

interface Usuario {
  readonly sub: string;
  readonly email: string;
  readonly ativo: boolean;
  readonly perfil: string | null;
  readonly customRole: string | null;
  readonly criadoEm: string;
  readonly nome: string | null;
  readonly sobrenome: string | null;
  readonly cargo: string | null;
  readonly telefone: string | null;
  readonly fotoUrl: string | null;
  readonly trilhas: readonly string[];
}

type Perfil = 'admin' | 'diretor' | 'vendedor' | 'leitor';

const PERFIS: readonly Perfil[] = ['admin', 'diretor', 'vendedor', 'leitor'];

type Coluna = 'nome' | 'cargo' | 'perfil' | 'situacao';

// ---------------------------------------------------------------------------
// Apresentação
// ---------------------------------------------------------------------------

const ESTILO_PERFIL: Readonly<Record<string, string>> = {
  admin: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  diretor: 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  vendedor: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  leitor: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

function BadgePerfil({ perfil }: { readonly perfil: string | null }) {
  const nome = perfil ?? 'sem perfil';
  const classe =
    ESTILO_PERFIL[nome] ?? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${classe}`}
    >
      {nome}
    </span>
  );
}

/** Extrai as iniciais para o avatar fallback. */
function iniciais(nome: string | null, sobrenome: string | null): string {
  const n = nome?.charAt(0) ?? '';
  const s = sobrenome?.charAt(0) ?? '';
  return (n + s).toUpperCase() || '?';
}

// ---------------------------------------------------------------------------
// Avatar com fallback
// ---------------------------------------------------------------------------

function Avatar({
  url,
  nome,
  sobrenome,
  classe,
}: {
  readonly url: string | null;
  readonly nome: string | null;
  readonly sobrenome: string | null;
  readonly classe?: string;
}) {
  const [falhou, setFalhou] = useState(false);
  const tamanho = classe ?? 'h-9 w-9';

  if (url && !falhou) {
    return (
      <img
        src={url}
        alt=""
        className={`${tamanho} rounded-full object-cover`}
        onError={() => {
          setFalhou(true);
        }}
      />
    );
  }

  return (
    <div
      className={`flex ${tamanho} items-center justify-center rounded-full bg-brand-blue text-white text-xs font-bold`}
    >
      {iniciais(nome, sobrenome)}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ícone de olho (mostrar/ocultar senha)
// ---------------------------------------------------------------------------

function IconeOlho({ aberto }: { readonly aberto: boolean }) {
  if (aberto) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-5"
        viewBox="0 0 20 20"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
        <path
          fillRule="evenodd"
          d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
          clipRule="evenodd"
        />
      </svg>
    );
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-5 w-5"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z"
        clipRule="evenodd"
      />
      <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Redimensionamento de foto
// ---------------------------------------------------------------------------

async function redimensionar(arquivo: File): Promise<string> {
  const bitmap = await createImageBitmap(arquivo);
  const lado = 256;
  const canvas = document.createElement('canvas');
  canvas.width = lado;
  canvas.height = lado;
  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('Nao foi possivel processar a imagem.');
  // Recorte central, para o rosto nao ficar deslocado no circulo.
  const menor = Math.min(bitmap.width, bitmap.height);
  const sx = (bitmap.width - menor) / 2;
  const sy = (bitmap.height - menor) / 2;
  ctx.drawImage(bitmap, sx, sy, menor, menor, 0, 0, lado, lado);
  return canvas.toDataURL('image/png');
}

// ---------------------------------------------------------------------------
// Ordenação
// ---------------------------------------------------------------------------

function obterValorOrdenacao(u: Usuario, coluna: Coluna): string {
  switch (coluna) {
    case 'nome':
      return (u.nome ?? u.email).toLowerCase();
    case 'cargo':
      return (u.cargo ?? '').toLowerCase();
    case 'perfil':
      return (u.perfil ?? 'zzz').toLowerCase();
    case 'situacao':
      return u.ativo ? '0' : '1';
  }
}

function ordenar(lista: Usuario[], ordem: { coluna: Coluna; asc: boolean } | null): Usuario[] {
  const copia = [...lista];

  if (ordem === null) {
    // Ordenação padrão: ativos primeiro, depois nome A-Z
    copia.sort((a, b) => {
      if (a.ativo !== b.ativo) return a.ativo ? -1 : 1;
      const nomeA = (a.nome ?? a.email).toLowerCase();
      const nomeB = (b.nome ?? b.email).toLowerCase();
      return nomeA.localeCompare(nomeB);
    });
    return copia;
  }

  copia.sort((a, b) => {
    const va = obterValorOrdenacao(a, ordem.coluna);
    const vb = obterValorOrdenacao(b, ordem.coluna);
    const cmp = va.localeCompare(vb);
    return ordem.asc ? cmp : -cmp;
  });

  return copia;
}

// ---------------------------------------------------------------------------
// Formulário de cadastro
// ---------------------------------------------------------------------------

const CARGOS_SUGERIDOS = [
  'Inside Sales',
  'Account Manager',
  'Public Sector Account Manager',
  'Sales Director',
  'AWS Solution Advisor',
  'Customer Success',
  'Pre-Sales Engineer',
  'Diretoria eMaster',
];

interface DadosCadastro {
  email: string;
  nome: string;
  sobrenome: string;
  cargo: string;
  telefone: string;
  perfil: string;
  trilhas: string;
  senhaTemporaria: string;
}

const DADOS_VAZIOS: DadosCadastro = {
  email: '',
  nome: '',
  sobrenome: '',
  cargo: '',
  telefone: '',
  perfil: 'vendedor',
  trilhas: 'CS',
  senhaTemporaria: '',
};

function FormularioCadastro({
  onSucesso,
  onCancelar,
}: {
  readonly onSucesso: () => void;
  readonly onCancelar: () => void;
}) {
  const [dados, setDados] = useState<DadosCadastro>(DADOS_VAZIOS);
  const [erros, setErros] = useState<Partial<Record<keyof DadosCadastro, string>>>({});
  const [enviando, setEnviando] = useState(false);
  const [erroApi, setErroApi] = useState<string | null>(null);
  const [mostrarSenha, setMostrarSenha] = useState(false);

  function campo<K extends keyof DadosCadastro>(chave: K, valor: DadosCadastro[K]) {
    setDados((prev) => ({ ...prev, [chave]: valor }));
    setErros((prev) => ({ ...prev, [chave]: undefined }));
  }

  function validar(): boolean {
    const novos: Partial<Record<keyof DadosCadastro, string>> = {};

    if (!dados.email) novos.email = 'Obrigatório';
    else if (!dados.email.endsWith('@emaster.info'))
      novos.email = 'O e-mail deve terminar em @emaster.info';

    if (!dados.nome.trim()) novos.nome = 'Obrigatório';
    if (!dados.sobrenome.trim()) novos.sobrenome = 'Obrigatório';
    if (!dados.cargo.trim()) novos.cargo = 'Obrigatório';

    // Telefone: se preenchido, valida como celular brasileiro.
    if (dados.telefone.trim() !== '') {
      const resultado = normalizarTelefoneBr(dados.telefone);
      if (!resultado.valido) {
        novos.telefone = resultado.motivo;
      }
    }

    if (!dados.senhaTemporaria) novos.senhaTemporaria = 'Obrigatório';
    else if (dados.senhaTemporaria.length < 8) novos.senhaTemporaria = 'Mínimo 8 caracteres';

    setErros(novos);
    return Object.keys(novos).length === 0;
  }

  async function submeter() {
    if (!validar()) return;
    setEnviando(true);
    setErroApi(null);

    try {
      const trilhasArray: string[] = dados.trilhas === 'AMBAS' ? ['CS', 'PS'] : [dados.trilhas];
      const resp = await criarUsuario({
        email: dados.email,
        nome: dados.nome,
        sobrenome: dados.sobrenome,
        cargo: dados.cargo,
        telefone: dados.telefone,
        perfil: dados.perfil,
        trilhas: trilhasArray,
        senhaTemporaria: dados.senhaTemporaria,
      });
      if (!resp.ok) {
        const msg =
          (resp.dados as { mensagem?: string } | undefined)?.mensagem ?? 'Falha ao criar usuário.';
        setErroApi(msg);
      } else {
        onSucesso();
      }
    } catch {
      setErroApi('Erro de conexão ao criar usuário.');
    } finally {
      setEnviando(false);
    }
  }

  const estiloInput =
    'w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-blue';
  const estiloLabel = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
  const estiloErro = 'text-xs text-red-600 mt-0.5';

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-6 space-y-4">
      <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Novo Usuário</h2>

      <p className="text-xs text-gray-500 dark:text-gray-400">
        O usuário receberá uma senha temporária e deverá trocá-la no primeiro acesso.
      </p>

      {erroApi && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {erroApi}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* E-mail */}
        <div className="sm:col-span-2">
          <label htmlFor="cadastro-email" className={estiloLabel}>
            E-mail <span className="text-red-500">*</span>
          </label>
          <input
            id="cadastro-email"
            type="email"
            value={dados.email}
            onChange={(e) => {
              campo('email', e.target.value);
            }}
            placeholder="usuario@emaster.info"
            className={estiloInput}
          />
          {erros.email && <p className={estiloErro}>{erros.email}</p>}
        </div>

        {/* Nome */}
        <div>
          <label htmlFor="cadastro-nome" className={estiloLabel}>
            Nome <span className="text-red-500">*</span>
          </label>
          <input
            id="cadastro-nome"
            type="text"
            value={dados.nome}
            onChange={(e) => {
              campo('nome', e.target.value);
            }}
            className={estiloInput}
          />
          {erros.nome && <p className={estiloErro}>{erros.nome}</p>}
        </div>

        {/* Sobrenome */}
        <div>
          <label htmlFor="cadastro-sobrenome" className={estiloLabel}>
            Sobrenome <span className="text-red-500">*</span>
          </label>
          <input
            id="cadastro-sobrenome"
            type="text"
            value={dados.sobrenome}
            onChange={(e) => {
              campo('sobrenome', e.target.value);
            }}
            className={estiloInput}
          />
          {erros.sobrenome && <p className={estiloErro}>{erros.sobrenome}</p>}
        </div>

        {/* Cargo */}
        <div>
          <label htmlFor="cadastro-cargo" className={estiloLabel}>
            Cargo <span className="text-red-500">*</span>
          </label>
          <input
            id="cadastro-cargo"
            type="text"
            list="cargos-sugeridos-cadastro"
            value={dados.cargo}
            onChange={(e) => {
              campo('cargo', e.target.value);
            }}
            className={estiloInput}
          />
          <datalist id="cargos-sugeridos-cadastro">
            {CARGOS_SUGERIDOS.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
          {erros.cargo && <p className={estiloErro}>{erros.cargo}</p>}
        </div>

        {/* Telefone */}
        <div>
          <label htmlFor="cadastro-telefone" className={estiloLabel}>
            Telefone
          </label>
          <div className="flex items-center rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 focus-within:ring-2 focus-within:ring-brand-blue">
            <span className="select-none pl-3 text-sm text-gray-500 dark:text-gray-400">+55</span>
            <input
              id="cadastro-telefone"
              type="tel"
              inputMode="numeric"
              autoComplete="tel-national"
              maxLength={13}
              value={dados.telefone}
              onChange={(e) => {
                campo('telefone', aplicarMascaraTelefoneBr(e.target.value));
              }}
              placeholder="31 99125-9696"
              className="w-full bg-transparent px-2 py-2 text-sm text-gray-800 dark:text-gray-100 focus:outline-none"
            />
          </div>
          <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
            Celular — aparece no botão de WhatsApp da proposta.
          </p>
          {erros.telefone && (
            <p role="alert" className={estiloErro}>
              {erros.telefone}
            </p>
          )}
        </div>

        {/* Perfil */}
        <div>
          <label htmlFor="cadastro-perfil" className={estiloLabel}>
            Perfil
          </label>
          <select
            id="cadastro-perfil"
            value={dados.perfil}
            onChange={(e) => {
              campo('perfil', e.target.value);
            }}
            className={estiloInput}
          >
            {PERFIS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>

        {/* Trilha */}
        <div className="sm:col-span-2">
          <label htmlFor="cadastro-trilha" className={estiloLabel}>
            Trilha
          </label>
          <select
            id="cadastro-trilha"
            value={dados.trilhas}
            onChange={(e) => {
              campo('trilhas', e.target.value);
            }}
            className={estiloInput}
          >
            <option value="CS">CS — Commercial Sector</option>
            <option value="PS">PS — Public Sector</option>
            <option value="AMBAS">CS e PS</option>
          </select>
        </div>

        {/* Senha temporária */}
        <div className="sm:col-span-2">
          <label htmlFor="cadastro-senha" className={estiloLabel}>
            Senha temporária <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              id="cadastro-senha"
              type={mostrarSenha ? 'text' : 'password'}
              value={dados.senhaTemporaria}
              onChange={(e) => {
                campo('senhaTemporaria', e.target.value);
              }}
              className={estiloInput}
            />
            <button
              type="button"
              onClick={() => {
                setMostrarSenha((v) => !v);
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-600"
              aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
            >
              <IconeOlho aberto={mostrarSenha} />
            </button>
          </div>
          {erros.senhaTemporaria && <p className={estiloErro}>{erros.senhaTemporaria}</p>}
        </div>
      </div>

      <div className="flex items-center gap-3 pt-2">
        <button
          type="button"
          disabled={enviando}
          onClick={() => {
            void submeter();
          }}
          className="rounded-lg bg-brand-blue px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          {enviando ? 'Cadastrando…' : 'Cadastrar'}
        </button>
        <button
          type="button"
          onClick={onCancelar}
          className="rounded-lg border border-gray-200 dark:border-gray-600 px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cabeçalho de coluna ordenável
// ---------------------------------------------------------------------------

function CabecalhoOrdenavel({
  label,
  coluna,
  ordemAtual,
  onClick,
}: {
  readonly label: string;
  readonly coluna: Coluna;
  readonly ordemAtual: { coluna: Coluna; asc: boolean } | null;
  readonly onClick: (col: Coluna) => void;
}) {
  const ativo = ordemAtual?.coluna === coluna;
  const seta = ativo ? (ordemAtual.asc ? ' ▲' : ' ▼') : '';

  return (
    <th
      scope="col"
      className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-200 cursor-pointer select-none hover:text-brand-blue"
      onClick={() => {
        onClick(coluna);
      }}
    >
      {label}
      {seta}
    </th>
  );
}

// ---------------------------------------------------------------------------
// Painel de edição expansível
// ---------------------------------------------------------------------------

interface DadosEdicao {
  nome: string;
  sobrenome: string;
  cargo: string;
  telefone: string;
  perfil: string;
  trilhas: string;
  ativo: boolean;
  fotoPreview: string | null;
  fotoBase64: string | null;
  fotoRemovida: boolean;
}

function PainelEdicao({
  usuario,
  proprio,
  onSalvar,
  onCancelar,
  onExcluir,
}: {
  readonly usuario: Usuario;
  readonly proprio: boolean;
  readonly onSalvar: (sub: string, dados: Record<string, unknown>) => Promise<void>;
  readonly onCancelar: () => void;
  readonly onExcluir: (sub: string, email: string) => void;
}) {
  const trilhasValor: string =
    usuario.trilhas.includes('CS') && usuario.trilhas.includes('PS')
      ? 'AMBAS'
      : usuario.trilhas.includes('PS')
        ? 'PS'
        : 'CS';

  const [dados, setDados] = useState<DadosEdicao>({
    nome: usuario.nome ?? '',
    sobrenome: usuario.sobrenome ?? '',
    cargo: usuario.cargo ?? '',
    /* Converte o formato de exibição do banco (+55 31 99125-9696) para o campo (31 99125-9696). */
    telefone: usuario.telefone ? exibicaoParaCampo(usuario.telefone) : '',
    perfil: usuario.perfil ?? 'vendedor',
    trilhas: trilhasValor,
    ativo: usuario.ativo,
    fotoPreview: usuario.fotoUrl,
    fotoBase64: null,
    fotoRemovida: false,
  });
  const [problemasFoto, setProblemasFoto] = useState<readonly ProblemaFoto[]>([]);
  const [erroTelefone, setErroTelefone] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  function campoTexto(chave: keyof DadosEdicao, valor: string) {
    setDados((prev) => ({ ...prev, [chave]: valor }));
  }

  async function aoEscolherFoto(e: React.ChangeEvent<HTMLInputElement>) {
    const arquivo = e.target.files?.[0];
    if (arquivo === undefined) return;

    // Validar com as regras do domínio. Precisamos criar um bitmap para obter dimensões.
    const bitmap = await createImageBitmap(arquivo);
    const candidata: FotoCandidata = {
      tipo: arquivo.type,
      tamanhoBytes: arquivo.size,
      largura: bitmap.width,
      altura: bitmap.height,
    };

    const problemas = validarFoto(candidata);
    setProblemasFoto(problemas);

    if (problemas.length > 0) return;

    const dataUrl = await redimensionar(arquivo);
    setDados((prev) => ({
      ...prev,
      fotoPreview: dataUrl,
      fotoBase64: dataUrl,
      fotoRemovida: false,
    }));
  }

  function removerFoto() {
    setDados((prev) => ({ ...prev, fotoPreview: null, fotoBase64: null, fotoRemovida: true }));
    setProblemasFoto([]);
  }

  async function salvar() {
    /* Validar telefone antes de enviar. */
    if (dados.telefone.trim() !== '') {
      const resultado = normalizarTelefoneBr(dados.telefone);
      if (!resultado.valido) {
        setErroTelefone(resultado.motivo);
        return;
      }
    }
    setErroTelefone(null);

    setSalvando(true);

    const mudancas: Record<string, unknown> = {};

    if (dados.nome !== (usuario.nome ?? '')) mudancas.nome = dados.nome;
    if (dados.sobrenome !== (usuario.sobrenome ?? '')) mudancas.sobrenome = dados.sobrenome;
    if (dados.cargo !== (usuario.cargo ?? '')) mudancas.cargo = dados.cargo;

    /* Envia o valor do campo direto — a API normaliza e deriva o whatsapp. */
    const telefoneOriginal = usuario.telefone ? exibicaoParaCampo(usuario.telefone) : '';
    if (dados.telefone !== telefoneOriginal) mudancas.telefone = dados.telefone;

    if (!proprio) {
      if (dados.perfil !== (usuario.perfil ?? 'vendedor')) mudancas.perfil = dados.perfil;
      if (dados.ativo !== usuario.ativo) mudancas.ativo = dados.ativo;
    }

    /*
     * Trilha: editável inclusive no próprio, porque não concede acesso — escolhe a moeda e a
     * regra de margem do cálculo. Perfil e situação continuam barrados no próprio.
     */
    const novasTrilhas: string[] = dados.trilhas === 'AMBAS' ? ['CS', 'PS'] : [dados.trilhas];
    const trilhasAntigas = [...usuario.trilhas].sort().join(',');
    const trilhasNovas = [...novasTrilhas].sort().join(',');
    if (trilhasNovas !== trilhasAntigas) mudancas.trilhas = novasTrilhas;

    if (dados.fotoBase64 !== null) {
      mudancas.fotoBase64 = dados.fotoBase64;
    } else if (dados.fotoRemovida) {
      mudancas.fotoUrl = null;
    }

    try {
      await onSalvar(usuario.sub, mudancas);
    } finally {
      setSalvando(false);
    }
  }

  const estiloInput =
    'w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-blue';
  const estiloLabel = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

  return (
    <tr>
      <td colSpan={7} className="px-4 py-5 bg-gray-50 dark:bg-gray-800/60">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-[auto_1fr]">
          {/* Coluna esquerda — Foto */}
          <div className="flex flex-col items-center gap-3">
            <Avatar
              url={dados.fotoPreview}
              nome={dados.nome || usuario.nome}
              sobrenome={dados.sobrenome || usuario.sobrenome}
              classe="h-24 w-24"
            />

            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => {
                void aoEscolherFoto(e);
              }}
              className="text-xs text-gray-500 dark:text-gray-400 file:mr-2 file:rounded file:border-0 file:bg-brand-blue file:px-2 file:py-1 file:text-xs file:text-white file:cursor-pointer"
            />

            {dados.fotoPreview && (
              <button
                type="button"
                onClick={removerFoto}
                className="text-xs text-red-600 hover:underline"
              >
                Remover foto
              </button>
            )}

            {problemasFoto.length > 0 && (
              <div className="text-xs text-red-600 space-y-0.5">
                {problemasFoto.map((p) => (
                  <p key={p.codigo}>{p.mensagem}</p>
                ))}
              </div>
            )}
          </div>

          {/* Coluna direita — Dados */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor={`edicao-${usuario.sub}-nome`} className={estiloLabel}>
                Nome
              </label>
              <input
                id={`edicao-${usuario.sub}-nome`}
                type="text"
                value={dados.nome}
                onChange={(e) => {
                  campoTexto('nome', e.target.value);
                }}
                className={estiloInput}
              />
            </div>

            <div>
              <label htmlFor={`edicao-${usuario.sub}-sobrenome`} className={estiloLabel}>
                Sobrenome
              </label>
              <input
                id={`edicao-${usuario.sub}-sobrenome`}
                type="text"
                value={dados.sobrenome}
                onChange={(e) => {
                  campoTexto('sobrenome', e.target.value);
                }}
                className={estiloInput}
              />
            </div>

            <div>
              <label htmlFor={`edicao-${usuario.sub}-cargo`} className={estiloLabel}>
                Cargo
              </label>
              <input
                id={`edicao-${usuario.sub}-cargo`}
                type="text"
                list="cargos-sugeridos-edicao"
                value={dados.cargo}
                onChange={(e) => {
                  campoTexto('cargo', e.target.value);
                }}
                className={estiloInput}
              />
              <datalist id="cargos-sugeridos-edicao">
                {CARGOS_SUGERIDOS.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </div>

            <div>
              <label htmlFor={`edicao-${usuario.sub}-telefone`} className={estiloLabel}>
                Telefone
              </label>
              <div className="flex items-center rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 focus-within:ring-2 focus-within:ring-brand-blue">
                <span className="select-none pl-3 text-sm text-gray-500 dark:text-gray-400">
                  +55
                </span>
                <input
                  id={`edicao-${usuario.sub}-telefone`}
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  maxLength={13}
                  value={dados.telefone}
                  onChange={(e) => {
                    campoTexto('telefone', aplicarMascaraTelefoneBr(e.target.value));
                    setErroTelefone(null);
                  }}
                  placeholder="31 99125-9696"
                  className="w-full bg-transparent px-2 py-2 text-sm text-gray-800 dark:text-gray-100 focus:outline-none"
                />
              </div>
              <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
                Celular — aparece no botão de WhatsApp da proposta.
              </p>
              {erroTelefone && (
                <p role="alert" className="text-xs text-red-600 mt-0.5">
                  {erroTelefone}
                </p>
              )}
            </div>

            <div>
              <label htmlFor={`edicao-${usuario.sub}-perfil`} className={estiloLabel}>
                Perfil
              </label>
              {proprio ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
                  {dados.perfil} — só outro administrador altera o seu perfil.
                </p>
              ) : (
                <select
                  id={`edicao-${usuario.sub}-perfil`}
                  value={dados.perfil}
                  onChange={(e) => {
                    setDados((prev) => ({ ...prev, perfil: e.target.value }));
                  }}
                  className={estiloInput}
                >
                  {PERFIS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label htmlFor={`edicao-${usuario.sub}-trilha`} className={estiloLabel}>
                Trilha
              </label>
              <select
                id={`edicao-${usuario.sub}-trilha`}
                value={dados.trilhas}
                onChange={(e) => {
                  setDados((prev) => ({
                    ...prev,
                    trilhas: e.target.value,
                  }));
                }}
                className={estiloInput}
              >
                <option value="CS">CS - Commercial Sector</option>
                <option value="PS">PS - Public Sector</option>
                <option value="AMBAS">CS e PS</option>
              </select>
            </div>

            <div>
              <label htmlFor={`edicao-${usuario.sub}-situacao`} className={estiloLabel}>
                Situação
              </label>
              {proprio ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
                  {dados.ativo ? 'Ativo' : 'Desativado'} — só outro administrador altera a sua
                  situação.
                </p>
              ) : (
                <select
                  id={`edicao-${usuario.sub}-situacao`}
                  value={dados.ativo ? 'ativo' : 'desativado'}
                  onChange={(e) => {
                    setDados((prev) => ({ ...prev, ativo: e.target.value === 'ativo' }));
                  }}
                  className={estiloInput}
                >
                  <option value="ativo">Ativo</option>
                  <option value="desativado">Desativado</option>
                </select>
              )}
            </div>
          </div>
        </div>

        {proprio && (
          <p className="mt-3 text-xs text-amber-600 dark:text-amber-400">
            Perfil e situação só podem ser alterados por outro administrador. A trilha você edita
            normalmente.
          </p>
        )}

        {/* Rodapé */}
        <div className="mt-5 flex items-center gap-3">
          <button
            type="button"
            disabled={salvando}
            onClick={() => {
              void salvar();
            }}
            className="rounded-lg bg-brand-accent px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {salvando ? 'Salvando…' : 'Salvar alterações'}
          </button>

          <button
            type="button"
            onClick={onCancelar}
            className="rounded-lg border border-gray-200 dark:border-gray-600 px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancelar
          </button>

          <div className="ml-auto">
            <button
              type="button"
              disabled={proprio}
              onClick={() => {
                onExcluir(usuario.sub, usuario.email);
              }}
              className="rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/30 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Excluir usuário
            </button>
          </div>
        </div>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export function TelaUsuarios() {
  const { sessao } = useAutenticacao();
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [ordem, setOrdem] = useState<{ coluna: Coluna; asc: boolean } | null>(null);
  const [mostrarCadastro, setMostrarCadastro] = useState(false);
  const [editando, setEditando] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const resp = await listarUsuarios();
      if (!resp.ok) {
        setErro('Não foi possível carregar a lista de usuários.');
        return;
      }
      const dados = resp.dados as { usuarios: Usuario[] };
      setUsuarios(dados.usuarios);
    } catch {
      setErro('Erro de conexão ao carregar usuários.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  // -------------------------------------------------------------------------
  // Ações
  // -------------------------------------------------------------------------

  const salvarHandler = useCallback(
    async (sub: string, mudancas: Record<string, unknown>) => {
      if (Object.keys(mudancas).length === 0) {
        setEditando(null);
        return;
      }

      try {
        const resp = await alterarUsuario(sub, mudancas);
        if (!resp.ok) {
          const msg =
            (resp.dados as { mensagem?: string } | undefined)?.mensagem ??
            'Falha ao salvar alterações.';
          setErro(msg);
        } else {
          setEditando(null);
        }
      } catch {
        setErro('Erro de conexão ao salvar alterações.');
      }
      void carregar();
    },
    [carregar],
  );

  const excluirHandler = useCallback(
    (sub: string, email: string) => {
      const confirma = window.confirm(
        `Tem certeza que deseja excluir o usuário ${email}? Esta ação não pode ser desfeita.`,
      );
      if (!confirma) return;

      void (async () => {
        try {
          const resp = await excluirUsuario(sub);
          if (!resp.ok) {
            setErro('Falha ao excluir usuário.');
          } else {
            setEditando(null);
          }
        } catch {
          setErro('Erro de conexão ao excluir usuário.');
        }
        void carregar();
      })();
    },
    [carregar],
  );

  // -------------------------------------------------------------------------
  // Ordenação
  // -------------------------------------------------------------------------

  function clicarColuna(coluna: Coluna) {
    setOrdem((prev) => {
      if (prev?.coluna === coluna) {
        return { coluna, asc: !prev.asc };
      }
      return { coluna, asc: true };
    });
  }

  const usuariosOrdenados = ordenar(usuarios, ordem);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-brand-blue dark:text-white">Gestão de Usuários</h1>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setMostrarCadastro((v) => !v);
            }}
            className="rounded-lg bg-brand-blue px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
          >
            Novo usuário
          </button>
          <button
            type="button"
            onClick={() => {
              void carregar();
            }}
            disabled={carregando}
            className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            Atualizar
          </button>
        </div>
      </div>

      {/* Formulário de cadastro expansível */}
      {mostrarCadastro && (
        <FormularioCadastro
          onSucesso={() => {
            setMostrarCadastro(false);
            void carregar();
          }}
          onCancelar={() => {
            setMostrarCadastro(false);
          }}
        />
      )}

      {erro && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {erro}
          <button
            type="button"
            onClick={() => {
              setErro(null);
            }}
            className="ml-3 font-medium underline"
          >
            Fechar
          </button>
        </div>
      )}

      {carregando ? (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-brand-blue" />
          <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">
            Carregando usuários…
          </span>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-200"
                  >
                    Foto
                  </th>
                  <CabecalhoOrdenavel
                    label="Nome"
                    coluna="nome"
                    ordemAtual={ordem}
                    onClick={clicarColuna}
                  />
                  <CabecalhoOrdenavel
                    label="Cargo"
                    coluna="cargo"
                    ordemAtual={ordem}
                    onClick={clicarColuna}
                  />
                  <CabecalhoOrdenavel
                    label="Perfil"
                    coluna="perfil"
                    ordemAtual={ordem}
                    onClick={clicarColuna}
                  />
                  <th
                    scope="col"
                    className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-200"
                  >
                    Trilha
                  </th>
                  <CabecalhoOrdenavel
                    label="Situação"
                    coluna="situacao"
                    ordemAtual={ordem}
                    onClick={clicarColuna}
                  />
                  <th
                    scope="col"
                    className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-200"
                  >
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {usuariosOrdenados.map((u) => {
                  const proprio = u.sub === sessao?.sub;

                  return (
                    <Fragment key={u.sub}>
                      <tr
                        className={`${proprio ? 'bg-blue-50/30 dark:bg-blue-900/10' : ''} ${!u.ativo ? 'opacity-60' : ''}`}
                      >
                        {/* Foto */}
                        <td className="px-4 py-3">
                          <Avatar url={u.fotoUrl} nome={u.nome} sobrenome={u.sobrenome} />
                        </td>

                        {/* Nome */}
                        <td className="px-4 py-3">
                          <div
                            className={`font-medium ${u.ativo ? 'text-gray-900 dark:text-gray-200' : 'text-gray-500 dark:text-gray-500'}`}
                          >
                            {u.nome || u.sobrenome
                              ? `${u.nome ?? ''} ${u.sobrenome ?? ''}`.trim()
                              : u.email}
                            {proprio && <span className="ml-2 text-xs text-gray-400">(você)</span>}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{u.email}</div>
                        </td>

                        {/* Cargo */}
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                          {u.cargo ?? '—'}
                        </td>

                        {/* Perfil */}
                        <td className="px-4 py-3">
                          <BadgePerfil perfil={u.perfil} />
                        </td>

                        {/* Trilha */}
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {u.trilhas.map((t) => (
                              <span
                                key={t}
                                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                  t === 'PS'
                                    ? 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
                                    : 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                }`}
                              >
                                {t}
                              </span>
                            ))}
                          </div>
                        </td>

                        {/* Situação */}
                        <td className="px-4 py-3">
                          {u.ativo ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-green-50 dark:bg-green-900/30 px-2.5 py-0.5 text-xs font-medium text-green-700 dark:text-green-300">
                              <span className="inline-block h-2 w-2 rounded-full bg-green-500" />
                              Ativo
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-700 px-2.5 py-0.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                              <span className="inline-block h-2 w-2 rounded-full bg-gray-400" />
                              Desativado
                            </span>
                          )}
                        </td>

                        {/* Ações */}
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => {
                              setEditando((prev) => (prev === u.sub ? null : u.sub));
                            }}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-600 px-3 py-1.5 text-xs font-semibold text-brand-blue dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.8}
                              stroke="currentColor"
                              className="h-4 w-4"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.077-.124.072-.044.146-.086.22-.128.331-.183.581-.495.644-.869l.213-1.28Z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                              />
                            </svg>
                            Editar
                          </button>
                        </td>
                      </tr>

                      {editando === u.sub && (
                        <PainelEdicao
                          usuario={u}
                          proprio={proprio}
                          onSalvar={salvarHandler}
                          onCancelar={() => {
                            setEditando(null);
                          }}
                          onExcluir={excluirHandler}
                        />
                      )}
                    </Fragment>
                  );
                })}

                {usuarios.length === 0 && (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-8 text-center text-gray-400 dark:text-gray-500"
                    >
                      Nenhum usuário encontrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
